import React from 'react';
import { graphql } from 'gatsby';
import { COMING_SOON, ENTERPRISE_DRM, STYLE_2024 } from '~/utils/constants';
import { cn, getSettingsClass } from '~/utils/helpers';

interface TextWithIconProps {
  block: {
    settings: Settings;
    _template: string;
    cta_field: CtaField;
    icon: string;
    is_active: boolean;
    pre_title: string;
    scale_on_hover: boolean;
    scale_by_default: {
      desktop: boolean;
      mobile: boolean;
    };
    text: string;
    title: string;
    // custom fields added (legacy mutation) when block is nested
    icon_position_class?: string;
    style?: string;
  };
  column_colors?: ColumnColors;
}

const TextWithIcon: React.FC<TextWithIconProps> = ({
  block,
  column_colors,
}) => {
  const settingsClass = getSettingsClass(block.settings);
  const scaleClass = block.scale_on_hover ? 'scale-on-hover' : '';
  const { is_active } = block;
  const isBlueLabel =
    block.style === COMING_SOON || block.style === ENTERPRISE_DRM;
  const labelText = block.style === COMING_SOON ? COMING_SOON : ENTERPRISE_DRM;
  const useNewDesign = block.style === STYLE_2024;

  // set default colors only if existing
  const style: {
    '--image-bg-color'?: string;
    '--hover-bg-color'?: string;
    '--active-bg-color'?: string;
    '--cta-font-size'?: string;
  } = {};
  if (column_colors && useNewDesign) {
    if (column_colors.image) style['--image-bg-color'] = column_colors.image;
    if (column_colors.hover) style['--hover-bg-color'] = column_colors.hover;
    if (column_colors.active) style['--active-bg-color'] = column_colors.active;
  }

  // use cta field font size if exists
  if (block.cta_field && block.cta_field.font_size && useNewDesign) {
    style['--cta-font-size'] = `${block.cta_field.font_size}rem`;
  }

  let scaleByDefault = '';
  if (block.scale_by_default) {
    if (block.scale_by_default.desktop === true) {
      scaleByDefault = `${scaleByDefault} scale-[1.2]`;
    }
    if (block.scale_by_default.mobile === true) {
      scaleByDefault = `${scaleByDefault} md:scale-[1.2]`;
    } else {
      scaleByDefault = `${scaleByDefault} md:scale-100`;
    }
  }

  return (
    <section
      className={cn(
        `block ${block._template} ${block.icon_position_class ? block.icon_position_class : ''} ${isBlueLabel ? 'style--coming-soon' : ''} ${is_active ? 'is-active' : ''} ${scaleByDefault} ${settingsClass}`
      )}
      style={style as React.CSSProperties}
    >
      {block.icon_position_class === 'icon-left' && (
        <div className='hover'>
          <span className='circle blue'></span>
          <span className='circle orange'></span>
        </div>
      )}
      {block.icon && (
        <div className='image-wr'>
          <img
            className={`card-image ${scaleClass}`}
            src={block.icon}
            alt={block.title}
            title={block.title}
          />
          {isBlueLabel && <span className='coming-soon'>{labelText}</span>}
        </div>
      )}
      <div className='content-wr'>
        {block.pre_title && block.icon_position_class !== 'icon-left' && (
          <div className='pre-title'>{block.pre_title}</div>
        )}
        <h3>
          <span dangerouslySetInnerHTML={{ __html: block.title }}></span>
          {block.pre_title && block.icon_position_class === 'icon-left' && (
            <div className='pre-title'>{block.pre_title}</div>
          )}
        </h3>
        {block.text && (
          <div
            className='text-formatted'
            dangerouslySetInnerHTML={{ __html: block.text }}
          ></div>
        )}
        {block.cta_field && (
          <a
            className={block.style === 'Link' ? 'cta-overlay' : 'cta-text'}
            href={block.cta_field.cta_url}
          >
            {block.style === 'Link' ? null : block.cta_field.cta_text}
          </a>
        )}
      </div>
    </section>
  );
};

export const textWithIconFragment = graphql`
  fragment TextWithIconFragment on TextWithIcon {
    _template
    settings {
      ...SettingsFragment
    }
    hide_on_mobile
    is_active
    style
    icon_position
    pre_title
    title
    text
    icon
    scale_on_hover
    scale_by_default {
      desktop
      mobile
    }
    cta_field {
      ...CtaFieldFragment
    }
  }
`;

export default TextWithIcon;
